import { BaseClass } from '../BaseClass.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { forEachKey, replaceTokens } from '@components/Utility/index.js';
import { forkJoin, of, Subject, take } from 'rxjs';
import { ENV } from '@components/Environment.js';
import { catchError, map, switchMap } from 'rxjs/operators';
/**
 * Class `CaptionManager` centralizes captions for Q360.
 * Code that utilizes captions should set captions via captionKeys instead of hardcoded captions, in support of multi-language captions
 */
export class CaptionManager extends BaseClass {
    get captions() { return this._captions; }
    cachePrefix() {
        return 'caption:';
    }
    cacheKey(captionKey) {
        return this.cachePrefix() + captionKey;
    }
    constructor(id = '', dependencies = {}) {
        super(id, dependencies);
        this._captions = {};
        this.captionKeys = {}; // not including the type, so we can track what modules have loaded.
        this._captionSubjects = {};
        this._captionLoadSubjects = {};
        this.requestQueue = {
            captionKey: { keys: new Set(), timer: null, subject: new Subject() }
        };
        this.requestDelay = 10; // 10ms
        this.cacheName = 'request';
        this.cacheManager = DIContainer.getInstance('CacheManager');
    }
    /**
     * Retrieves a caption based on the provided caption key.
     *
     * @param captionKey - The key associated with the desired caption.
     * @param params - Token values for complex captions that include tokens.
     * @returns The caption string associated with the provided key.
     *
     * @example
     * // Suppose captions contains: { "customer_customerno_label": "CustomerNo" }
     * const captionManager = s360.DIContainer.getInstance('CaptionManager');
     * const caption = captionManager.getCaption("customer_customerno_label");
     * console.log(caption); // Output: "CustomerNo"
     */
    getCaption(captionKey, params, options) {
        let caption = this._captions[captionKey];
        // most of the time, we just want the captionKey with type caption, for things like prompts etc. 
        if (!Reflect.has(this._captions, captionKey) && Reflect.has(this._captions, captionKey + '_caption')) {
            caption = this._captions[captionKey + '_caption'];
        }
        if (caption && caption.indexOf('{{') > -1) {
            caption = replaceTokens(caption, (params || {}), options);
        }
        return caption;
    }
    /**
     * any caption (ie. system level caption) that could possibly not be done loading yet should use getCaption$
     * @param captionKey
     * @param params
     */
    getCaption$(captionKey, params) {
        if (this._captions[captionKey]) {
            return of(this.getCaption(captionKey, params)).pipe(take(1));
        }
        else if (!this._captionSubjects[captionKey]) {
            const obs = new Subject();
            this._captionSubjects[captionKey] = obs;
        }
        return this._captionSubjects[captionKey].asObservable().pipe(take(1));
    }
    hasKey(captionKey) {
        return Reflect.has(this._captions, captionKey) || Reflect.has(this.captionKeys, captionKey);
    }
    addKey(captionKey) {
        // ie. some component loaded with a form, and it has a captionKey, but the database does not have any captions for this form. 
        // so therefore we need to flag this caption as loaded so we dont needlessly send out an AJAX call to load a caption that doesnt exist. 
        this.captionKeys[captionKey] = true;
    }
    /**
     * For specific captions (ie. captionkey_captiontype) use getCaption$.
     * onLoad$ is for for observing a collection of captions for a single system-level component that has multiple captions
     * @param captionKey
     * @param requestCaptions
     * @example
     * captionManager.onLoad$('system_toolbar')
     * .subscribe(captions => {
     *     let navigationCaption = captions.navigation; // assuming your caption is defined as captionKey 'system_toolbar' captionType 'navigation' in the database
     * })
     */
    onLoad$(captionKey, requestCaptions = false) {
        return DIContainer.getInstance('Application').configReady$().pipe(switchMap(() => {
            if (this.captionKeys[captionKey]) {
                // this captionkey is already loaded.
                // Filter captions that start with the captionKey
                const filteredCaptions = {};
                forEachKey(this._captions, (caption, key) => {
                    if (key.startsWith(captionKey + '_')) {
                        filteredCaptions[key.replace(captionKey + '_', '')] = caption;
                    }
                });
                // Emit the filtered captions
                return of(filteredCaptions).pipe(take(1));
            }
            else if (!this._captionLoadSubjects[captionKey]) {
                const obs = new Subject();
                this._captionLoadSubjects[captionKey] = obs;
                // we dont have this captionKey, check if we were told to ajax for it
                if (requestCaptions) {
                    this.requestCaptions(captionKey);
                }
            }
            // this subject will get next'd later in addCaptions.
            return this._captionLoadSubjects[captionKey].asObservable().pipe(take(1));
        }));
    }
    requestCaptions(captionKey) {
        this.requestQueue.captionKey.keys.add(captionKey);
        if (this.requestQueue.captionKey.timer) {
            clearTimeout(this.requestQueue.captionKey.timer);
        }
        this.requestQueue.captionKey.timer = setTimeout(() => {
            const queuedKeys = Array.from(this.requestQueue.captionKey.keys);
            // Filter out already-loaded keys
            const keysToResolve = queuedKeys.filter(k => !this.captionKeys[k]);
            this.readCaptionKeysFromCache(keysToResolve).pipe(switchMap(({ found, emptyKeys, missingKeys }) => {
                // Add cached captions immediately
                if (found.length) {
                    this.addCaptions(found);
                }
                // Mark negative-cache keys as loaded + resolve any onLoad$
                emptyKeys.forEach(k => {
                    this.captionKeys[k] = true;
                    if (this._captionLoadSubjects[k]) {
                        this._captionLoadSubjects[k].next({});
                        this._captionLoadSubjects[k].complete();
                        delete this._captionLoadSubjects[k];
                    }
                });
                // If nothing missing, we’re done
                if (!missingKeys.length) {
                    return of(null);
                }
                // network for missing only
                const captionKeyCSV = missingKeys.join(',');
                const request = DIContainer.getInstance('Request');
                return request.GET({
                    endpoint: '/caption/',
                    action: 'list',
                    showErrors: true,
                    requestData: { captionKey: captionKeyCSV, language: ENV('language') }
                }).pipe(map(response => ({ response, requestedKeys: missingKeys })));
            })).subscribe((result) => {
                if (!result) {
                    // this.requestQueue.captionKey.subject.next(response.payload.result);
                    return;
                }
                const { response, requestedKeys } = result;
                const captionResults = response.payload.result;
                // captionResults = captionResults.filter((cap) => cap.language === ENV('language'));
                const captions = captionResults.map((cap) => ({
                    captionKey: cap.captionkey,
                    captionType: cap.captiontype,
                    caption: cap.caption
                }));
                // Add to memory
                this.addCaptions(captions);
                // Group by base key for caching
                const grouped = {};
                captions.forEach(c => {
                    if (!grouped[c.captionKey])
                        grouped[c.captionKey] = {};
                    grouped[c.captionKey][c.captionType] = c.caption;
                });
                const batch = {};
                // Cache positives
                Object.keys(grouped).forEach(baseKey => {
                    batch[this.cacheKey(baseKey)] = this.buildCaptionCacheEntry(grouped[baseKey]);
                });
                requestedKeys.forEach(baseKey => {
                    if (!grouped[baseKey]) {
                        batch[this.cacheKey(baseKey)] = this.buildCaptionCacheEntry({});
                    }
                });
                if (Object.keys(batch).length) {
                    this.cacheManager.setCacheBatch(this.cacheName, batch).subscribe();
                }
                // resolve negatives in-memory + subjects
                requestedKeys.forEach(baseKey => {
                    if (!grouped[baseKey]) {
                        if (this._captionLoadSubjects[baseKey]) {
                            this._captionLoadSubjects[baseKey].next({});
                            this._captionLoadSubjects[baseKey].complete();
                            delete this._captionLoadSubjects[baseKey];
                        }
                        this.captionKeys[baseKey] = true;
                    }
                });
                // this.requestQueue.captionKey.subject.next(response.payload.result);
            });
            this.requestQueue.captionKey.keys.clear();
            this.requestQueue.captionKey.timer = null;
        }, this.requestDelay);
        // dont currently need the subject
        return this.requestQueue.captionKey.subject.pipe(take(1));
    }
    // private requestCaptions(captionKey: string) {
    //
    // 	this.requestQueue.captionKey.keys.add(captionKey);
    // 	if (this.requestQueue.captionKey.timer) {
    // 		clearTimeout(this.requestQueue.captionKey.timer);
    // 	}
    //
    // 	this.requestQueue.captionKey.timer = setTimeout(() => {
    //
    // 		const captionKeyCSV = Array.from(this.requestQueue.captionKey.keys).join(',');
    //
    // 		const request = DIContainer.getInstance<HttpRequestInterface>('Request');
    // 		request.GET({
    // 			endpoint: '/caption/'
    // 			,action: 'list'
    // 			,showErrors: true
    // 			,requestData: {
    // 				captionKey: captionKeyCSV
    // 			}
    // 		})
    // 			.subscribe((response) => {
    // 				let captionResults: {[key: string]: string}[] = response.payload.result;
    // 				captionResults = captionResults.filter((cap) => {
    // 					return cap.language === ENV('language')
    // 				})
    //
    // 				const captions = captionResults.map((cap) => {
    // 					return {captionKey: cap.captionkey, captionType: cap.captiontype, caption: cap.caption}
    // 				})
    //
    // 				this.addCaptions(captions);
    //
    // 				captionKeyCSV.split(',').forEach((captionKey) => {
    // 					if (!captions.length || !this.captionKeys[captionKey] && this._captionLoadSubjects[captionKey]) {
    // 						this._captionLoadSubjects[captionKey].next({});
    // 						this._captionLoadSubjects[captionKey].complete();
    // 						delete this._captionLoadSubjects[captionKey];
    // 					}
    // 				})
    //
    // 				return this.requestQueue.captionKey.subject.next(response.payload.result);
    // 			})
    //
    //
    //
    // 		this.requestQueue.captionKey.keys.clear();
    // 		this.requestQueue.captionKey.timer = null;
    //
    // 	}, this.requestDelay);
    //
    //
    // 	return this.requestQueue.captionKey.subject.pipe(take(1));
    // }
    //
    updateCaptions(captions) {
        // AJAX request to update the captions
        // update the captions in caption manager too
        console.log('Updating Captions: ', captions);
        this.addCaptions(captions);
        // update the cache
        // Group updates by baseKey
        const grouped = {};
        captions.forEach(c => {
            if (!grouped[c.captionKey])
                grouped[c.captionKey] = {};
            grouped[c.captionKey][c.captionType] = c.caption;
        });
        const baseKeys = Object.keys(grouped);
        if (!baseKeys.length)
            return;
        // Read existing cache for all baseKeys (so partial edits don't wipe other types)
        const reads$ = baseKeys.map(baseKey => this.cacheManager.getCache(this.cacheName, this.cacheKey(baseKey), { defaultReturnValue: null })
            .pipe(take(1), map(existing => ({ baseKey, existing })), catchError(() => of({ baseKey, existing: null }))));
        forkJoin(reads$).pipe(take(1)).subscribe(results => {
            const batch = {};
            results.forEach(({ baseKey, existing }) => {
                const existingTypes = existing?.types || {};
                const mergedTypes = { ...existingTypes, ...grouped[baseKey] };
                batch[this.cacheKey(baseKey)] = this.buildCaptionCacheEntry(mergedTypes);
            });
            this.cacheManager.setCacheBatch(this.cacheName, batch).subscribe();
        });
        const request = DIContainer.getInstance('Request');
        const requestData = {
            json: JSON.stringify({ data: { griddata: { record: captions } } })
        };
        request.PATCH({
            endpoint: '/caption/',
            showErrors: true,
            requestData: requestData
        })
            .subscribe(() => {
            // TODO: seek out any components that need to be updated with the new captions?
        });
    }
    /**
     * Loads captions from an array of caption objects into the captions dictionary.
     *
     * Each caption object should contain a captionKey, captionType, and caption.
     * The captions are stored with a key generated by combining captionKey and captionType.
     *
     * @param captionsArray - Array of objects each containing captionKey, captionType, and caption.
     *
     * @example
     * // Example of a captionsArray:
     * captionManager.addCaptions([
     *   { captionKey: "customer_customerno", captionType: "label", caption: "CustomerNo" }
     * ]);
     * // Now, captions contains: { "customer_customerno_label": "CustomerNo" }
     */
    addCaptions(captionsArray) {
        const keys = {};
        captionsArray.forEach(captionObj => {
            const key = `${captionObj.captionKey}_${captionObj.captionType}`;
            this._captions[key] = captionObj.caption;
            this.captionKeys[captionObj.captionKey] = true;
            if (this._captionLoadSubjects[captionObj.captionKey]) {
                if (!keys[captionObj.captionKey])
                    keys[captionObj.captionKey] = {};
                keys[captionObj.captionKey][captionObj.captionType] = captionObj.caption;
            }
            if (this._captionSubjects[key]) {
                this._captionSubjects[key].next(captionObj.caption);
                this._captionSubjects[key].complete();
                delete this._captionSubjects[key];
            }
        });
        forEachKey(keys, (val, key) => {
            this._captionLoadSubjects[key].next(val);
            this._captionLoadSubjects[key].complete();
            delete this._captionLoadSubjects[key];
        });
    }
    // Read many captionKeys from cache.
    readCaptionKeysFromCache(keys) {
        if (!keys.length) {
            return of({ found: [], emptyKeys: [], missingKeys: [] });
        }
        const reads$ = keys.map(k => this.cacheManager.getCache(this.cacheName, this.cacheKey(k), { defaultReturnValue: null })
            .pipe(map((entry) => ({ key: k, entry })), catchError(() => of({ key: k, entry: null }))));
        return forkJoin(reads$).pipe(map(results => {
            const found = [];
            const emptyKeys = [];
            const missingKeys = [];
            results.forEach(({ key, entry }) => {
                if (!entry) {
                    missingKeys.push(key);
                    return;
                }
                const types = entry.types || {};
                const typeNames = Object.keys(types);
                if (!typeNames.length) {
                    // negative cache hit
                    emptyKeys.push(key);
                    return;
                }
                typeNames.forEach(t => {
                    found.push({ captionKey: key, captionType: t, caption: types[t] });
                });
            });
            return { found, emptyKeys, missingKeys };
        }));
    }
    buildCaptionCacheEntry(types) {
        return {
            language: ENV('language'),
            types,
            ts: Date.now()
        };
    }
    // Write a captionKey's types to cache (positive or negative).
    writeCaptionKeyToCache(captionKey, types) {
        const entry = {
            language: ENV('language'),
            types,
            ts: Date.now()
        };
        return this.cacheManager.setCache(this.cacheName, this.cacheKey(captionKey), entry);
    }
    destroy() {
        for (const prop in this._captionSubjects) {
            this._captionSubjects[prop].complete();
        }
        this._captionSubjects = {};
        super.destroy();
    }
}
